import React, { useState } from "react";
import { PiExportBold } from "react-icons/pi";
import { FaFileCsv, FaFileExcel, FaFileCode } from "react-icons/fa";
import "../css/others/ExportButton.css";
import "../css/others/Table.css";
import ToastPopup from "../popups/ToastPopup";
import { ExportButtonProps, createToastMessage, ToastMessage } from '../interFaces/interfaces';
const ExportButton: React.FC<ExportButtonProps> = ({ data, columns, fetchAllData, fileName = "exported_data" }) => {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const exportData = async (format: string) => {
    setShowModal(false);
    let dataToExport = data;
    if (fetchAllData) {
      try {
        dataToExport = await fetchAllData();
        if (dataToExport.length === 0) {
          setMessage(createToastMessage("No data to export.", "warning", 3000));
          return;
        }
      } catch (error) {
        setMessage(createToastMessage("Failed to fetch data for export.", "error", 3000));
        return;
      }
    } else if (data.length === 0) {
      setMessage(createToastMessage("No data available to export.", "warning", 3000));
      return;
    }
    switch (format) {
      case "csv":
        exportToCSV(dataToExport);
        break;
      case "json":
        exportToJSON(dataToExport);
        break;
      case "xlsx":
        exportToXLSX(dataToExport);
        break;
      default:
        setMessage(createToastMessage("Unsupported export format.", "warning", 3000));
    }
  };
  const exportToCSV = (dataToExport: any[]) => {
    const headers = columns.map(col => col.label);
    const csvContent = [
      headers.join(","),
      ...dataToExport.map(item =>
        columns.map(col => `"${String(item[col.key] || "").replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");
    downloadFile(csvContent, "text/csv", `${fileName}_${getFormattedDate()}.csv`);
  };
  const exportToJSON = (dataToExport: any[]) => {
    const jsonData = dataToExport.map(item => {
      const jsonItem: any = {};
      columns.forEach(col => {
        const key = col.key === 'displayProjectId' ? 'projectId' : col.key;
        jsonItem[key] = item[key] || item[col.key];
      });
      return jsonItem;
    });
    const jsonContent = JSON.stringify(jsonData, null, 2);
    downloadFile(jsonContent, "application/json", `${fileName}_${getFormattedDate()}.json`);
  };
  const exportToXLSX = (dataToExport: any[]) => {
    let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>${fileName}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--><meta charset="UTF-8"></head><body><table>`;
    html += "<tr>";
    columns.forEach(col => {
      html += `<th>${col.label}</th>`;
    });
    html += "</tr>";
    dataToExport.forEach(item => {
      html += "<tr>";
      columns.forEach(col => {
        html += `<td>${item[col.key]}</td>`;
      });
      html += "</tr>";
    });
    html += "</table></body></html>";
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}_${getFormattedDate()}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const downloadFile = (content: string, mimeType: string, fileName: string) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const getFormattedDate = () => {
    return new Date().toISOString().split("T")[0];
  };
  return (
    <>
      <button className="export-button" onClick={() => setShowModal(true)}><PiExportBold size={23} /></button>
      {showModal && (
        <div className="export-modal-overlay">
          <div className="export-modal">
            <div className="export-modal-header">
              <h3>Export Data As</h3>
              <button className="close-button" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <div className="export-options">
              <button onClick={() => exportData("csv")}>
                <div className="export-option">
                  <FaFileCsv className="csv-icon" size={20} />
                  <div>
                    <div className="export-format">CSV</div>
                  </div>
                </div>
              </button>
              <button onClick={() => exportData("json")}>
                <div className="export-option">
                  <FaFileCode className="json-icon" size={20} />
                  <div>
                    <div className="export-format">JSON</div>
                  </div>
                </div>
              </button>
              <button onClick={() => exportData("xlsx")}>
                <div className="export-option">
                  <FaFileExcel className="excel-icon" size={20} />
                  <div>
                    <div className="export-format">Excel</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </>
  );
};
export default ExportButton;